type Phase = 'MATERIALIZE' | 'EXTRACT_GROW' | 'EXTRACT_HOLD' | 'EXTRACT_SNAP' | 'EMBED' | 'DISSIPATE';

interface CrystalShard {
    x: number;
    y: number;
    vx: number;
    vy: number;
    rotation: number;
    vr: number;
    size: number;
    life: number;
    maxLife: number;
    color: string;
}

interface HolyAura {
    x: number;
    y: number;
    radius: number;
    maxRadius: number;
    life: number;
    maxLife: number;
}

export interface PurificationState {
    id: string;
    sourceX: number; // Defender tower slot X (Where we steal from)
    sourceY: number; // Defender tower slot Y
    targetX: number; // Attacker tower slot X (Where it embeds)
    targetY: number; // Attacker tower slot Y

    // Calculated crystal center
    centerX: number;
    centerY: number;

    phase: Phase;
    progress: number;
    totalTime: number; // Persistent time counter for continuous sub-animations (flares)
    shards: CrystalShard[];
    auras: HolyAura[];
}

const CONSTANTS = {
    MATERIALIZE_DURATION: 1000,
    EXTRACT_GROW_DURATION: 300,
    EXTRACT_HOLD_DURATION: 1350, // Shortened to match original total extraction time (300+1350+150 = 1800ms)
    EXTRACT_SNAP_DURATION: 150,
    EMBED_DURATION: 1500, // Crystal moves to slot
    DISSIPATE_DURATION: 1000, // Flash and vanish
    CRYSTAL_SIZE: 400, // Massively increased to account for the expanded SVG viewBox (which holds the massive 4x flares)
    COLORS: ['#0ff', '#88ffff', '#00aaff', '#ffffff', '#00ccff']
};

export const createPurificationState = (
    id: string,
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number
): PurificationState => {
    // Determine dynamic center. 
    // If targetY (attacker's slot) is in the top half of the screen, the opponent cast it.
    // If targetY is in the bottom half, the local player cast it.
    // We want the crystal to hover centrally but slightly biased towards the caster's side of the river.
    const screenHeight = window.innerHeight;
    const isOpponent = targetY < screenHeight / 2;

    const centerX = window.innerWidth / 2;
    // Base center is middle of screen. Bias it 100px towards the caster.
    const centerY = (screenHeight / 2) + (isOpponent ? -100 : 100);

    return {
        id,
        sourceX,
        sourceY,
        targetX,
        targetY,
        centerX,
        centerY,
        phase: 'MATERIALIZE',
        progress: 0,
        totalTime: 0,
        shards: [],
        auras: []
    };
};

export const updatePurification = (
    state: PurificationState,
    dt: number,
    onComplete: () => void
): boolean => {
    state.progress += dt;
    state.totalTime += dt;

    // Phase logic
    switch (state.phase) {
        case 'MATERIALIZE':
            if (state.progress >= CONSTANTS.MATERIALIZE_DURATION) {
                state.phase = 'EXTRACT_GROW';
                state.progress = 0;
                // Spawn initial auras
                for (let i = 0; i < 3; i++) {
                    state.auras.push(createAura(state.centerX, state.centerY, 150 + i * 60));
                }
            }
            break;
        case 'EXTRACT_GROW':
            if (state.progress >= CONSTANTS.EXTRACT_GROW_DURATION) {
                state.phase = 'EXTRACT_HOLD';
                state.progress = 0;
            }
            break;
        case 'EXTRACT_HOLD':
            if (state.progress >= CONSTANTS.EXTRACT_HOLD_DURATION) {
                state.phase = 'EXTRACT_SNAP';
                state.progress = 0;
            } else {
                // Spawn extraction particles moving up the beam constantly
                if (Math.random() > 0.3) {
                    createExtractionShard(state, state.centerX, state.centerY);
                }
            }
            break;
        case 'EXTRACT_SNAP':
            if (state.progress >= CONSTANTS.EXTRACT_SNAP_DURATION) {
                state.phase = 'EMBED';
                state.progress = 0;
                // Flash aura before embedding
                state.auras.push(createAura(state.centerX, state.centerY, 250));
            }
            break;
        case 'EMBED':
            if (state.progress >= CONSTANTS.EMBED_DURATION) {
                state.phase = 'DISSIPATE';
                state.progress = 0;
                // Removed shatter per user request for optimization and clean look
                state.auras.push(createAura(state.targetX, state.targetY, 300));
            } else {
                // Trail particles as the crystal moves
                const embedRatio = Math.pow(state.progress / CONSTANTS.EMBED_DURATION, 2.5); // Ease-in
                const currentX = state.centerX + (state.targetX - state.centerX) * embedRatio;
                const currentY = state.centerY + (state.targetY - state.centerY) * embedRatio;

                if (Math.random() > 0.4) {
                    createTrailShard(state, currentX, currentY);
                }
            }
            break;
        case 'DISSIPATE':
            if (state.progress >= CONSTANTS.DISSIPATE_DURATION) {
                onComplete();
                return false;
            }
            break;
    }

    // Update auras
    state.auras.forEach(aura => {
        aura.life += dt;
        aura.radius += dt * 0.15;
    });
    state.auras = state.auras.filter(a => a.life < a.maxLife);

    // Update shards
    state.shards.forEach(shard => {
        shard.life += dt;
        shard.x += shard.vx * dt * 0.06;
        shard.y += shard.vy * dt * 0.06;
        shard.rotation += shard.vr * dt * 0.06;

        // Gravity for dissipate phase or dropping shards
        if (state.phase === 'DISSIPATE') {
            shard.vy += dt * 0.003;
        }
    });
    state.shards = state.shards.filter(s => s.life < s.maxLife);

    return true;
};

const createAura = (x: number, y: number, maxRadius: number): HolyAura => ({
    x, y,
    radius: 10,
    maxRadius,
    life: 0,
    maxLife: 600 + Math.random() * 400
});

const createExtractionShard = (state: PurificationState, cx: number, cy: number) => {
    const angle = Math.atan2(cy - state.sourceY, cx - state.sourceX);
    const speed = 12 + Math.random() * 8; // Faster
    state.shards.push({
        x: state.sourceX + (Math.random() - 0.5) * 60,
        y: state.sourceY + (Math.random() - 0.5) * 60,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.3,
        size: 4 + Math.random() * 8,
        life: 0,
        maxLife: 800,
        color: CONSTANTS.COLORS[Math.floor(Math.random() * CONSTANTS.COLORS.length)]
    });
};

const createTrailShard = (state: PurificationState, cx: number, cy: number) => {
    state.shards.push({
        x: cx + (Math.random() - 0.5) * 50,
        y: cy + (Math.random() - 0.5) * 50,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        rotation: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.2,
        size: 3 + Math.random() * 6,
        life: 0,
        maxLife: 500,
        color: CONSTANTS.COLORS[Math.floor(Math.random() * CONSTANTS.COLORS.length)]
    });
};

// Removed createShatter function for optimization and cleaner visuals

export const drawPurification = (
    ctx: CanvasRenderingContext2D,
    state: PurificationState,
    crystalImg: HTMLImageElement | null
) => {
    ctx.save();

    // 1. Draw Shards (Background Layer for extraction)
    drawShards(ctx, state);

    // 2. Draw Auras
    state.auras.forEach(aura => {
        const alpha = Math.max(0, 1 - (aura.life / aura.maxLife));
        ctx.beginPath();
        ctx.arc(aura.x, aura.y, aura.radius, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(aura.x, aura.y, 0, aura.x, aura.y, aura.radius);
        gradient.addColorStop(0, `rgba(136, 255, 255, ${alpha * 0.8})`); // Cyan/White core
        gradient.addColorStop(0.3, `rgba(0, 170, 255, ${alpha * 0.4})`); // Blue mid
        gradient.addColorStop(1, 'rgba(0, 34, 204, 0)');
        ctx.fillStyle = gradient;
        ctx.globalCompositeOperation = 'screen';
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
    });

    // 3. Draw Volumetric Beams (Rubber Band Logic)
    if (state.phase === 'EXTRACT_GROW' || state.phase === 'EXTRACT_HOLD' || state.phase === 'EXTRACT_SNAP') {
        let alpha = 1;
        let startX = state.sourceX;
        let startY = state.sourceY;
        let endX = state.centerX;
        let endY = state.centerY;

        if (state.phase === 'EXTRACT_GROW') {
            const ratio = Math.pow(state.progress / CONSTANTS.EXTRACT_GROW_DURATION, 2.0); // Accelerate reach out
            endX = state.sourceX + (state.centerX - state.sourceX) * ratio;
            endY = state.sourceY + (state.centerY - state.sourceY) * ratio;
            alpha = ratio;
        } else if (state.phase === 'EXTRACT_HOLD') {
            alpha = 1; // Pure blinding hold
        } else if (state.phase === 'EXTRACT_SNAP') {
            const ratio = Math.pow(state.progress / CONSTANTS.EXTRACT_SNAP_DURATION, 3.0); // Extreme snap back to crystal
            startX = state.sourceX + (state.centerX - state.sourceX) * ratio;
            startY = state.sourceY + (state.centerY - state.sourceY) * ratio;
            alpha = 1; // Keep bright all the way into the hand
        }

        drawVolumetricBeam(ctx, startX, startY, endX, endY, alpha);
    }

    // 4. Draw Main Crystal
    if (state.phase !== 'DISSIPATE' && crystalImg) {
        let alpha = 1;
        let scale = 1;
        let currentX = state.centerX;
        let currentY = state.centerY;

        // Base hover bobbing is constant for all phases
        const hoverBob = Math.sin(Date.now() / 250) * 12;

        if (state.phase === 'MATERIALIZE') {
            const ratio = Math.min(1, state.progress / CONSTANTS.MATERIALIZE_DURATION);
            alpha = ratio;
            const yOffset = hoverBob - 100 * (1 - Math.pow(ratio, 4)); // Drop in from high up
            currentY += yOffset;
            scale = 0.4 + 0.6 * Math.sin(ratio * Math.PI / 2); // Grow
            ctx.filter = `brightness(${1 + (1 - ratio) * 2})`; // Start blindingly bright
        } else if (state.phase === 'EXTRACT_GROW' || state.phase === 'EXTRACT_HOLD' || state.phase === 'EXTRACT_SNAP') {

            // Tension logic: The crystal gets pulled towards the beam source (like stretching gum)
            let tensionRatio = 1;
            if (state.phase === 'EXTRACT_GROW') {
                tensionRatio = Math.sin((state.progress / CONSTANTS.EXTRACT_GROW_DURATION) * (Math.PI / 2)); // Ease out
            }
            // in HOLD and SNAP, tensionRatio remains 1. The crystal stays pulled until the beam actually hits it.

            const dx = state.centerX - state.sourceX;
            const dy = state.centerY - state.sourceY;
            const dist = Math.hypot(dx, dy) || 1;
            const nx = dx / dist; // Normalized vector AWAY from source
            const ny = dy / dist;

            // Max stretch is 40 pixels towards the beam source (negative nx/ny direction)
            const pullAmount = 40 * tensionRatio;

            // Add a frantic shake during the HOLD phase to sell the chaotic tension
            const shake = state.phase === 'EXTRACT_HOLD' ? (Math.random() - 0.5) * 6 : 0;

            currentX += (-nx * pullAmount) + shake;
            currentY += (-ny * pullAmount) + shake + hoverBob;

            ctx.shadowBlur = 40 + Math.sin(Date.now() / 150) * 20;
            ctx.shadowColor = '#0ff';
        } else if (state.phase === 'EMBED') {
            // Move from center to target slot
            const ratio = Math.pow(state.progress / CONSTANTS.EMBED_DURATION, 2.5); // Accelerate towards slot
            const baseX = state.centerX + (state.targetX - state.centerX) * ratio;
            const baseY = state.centerY + (state.targetY - state.centerY) * ratio;

            // Impact Recoil logic: the snapped "gum" hits the crystal, knocking it violently AWAY from the source
            const dx = state.centerX - state.sourceX;
            const dy = state.centerY - state.sourceY;
            const dist = Math.hypot(dx, dy) || 1;
            const nx = dx / dist; // Vector AWAY from source
            const ny = dy / dist;

            // Damped spring physics: continuous from the -40px tension state.
            // s(0) = -40 (matches the tension pull from previous phase).
            // s peaks positively (pushed away violently), then damps to 0 over the first part of the transit.
            const ratioT = ratio * 15;
            const springS = -40 * Math.cos(ratioT) * Math.exp(-ratio * 8) + 120 * Math.sin(ratioT) * Math.exp(-ratio * 8);

            currentX = baseX + nx * springS;
            currentY = baseY + ny * springS + hoverBob * (1 - ratio); // Bob fades out as it embeds

            // Scale down to fit the slot perfectly
            // A tower slot is 56x56, crystal is CONSTANTS.CRYSTAL_SIZE
            const targetScale = 60 / CONSTANTS.CRYSTAL_SIZE;
            scale = 1 - (1 - targetScale) * ratio;

            // Impact Scale thump (Crystal absorbs the energy with a 30% scale expansion that decays)
            const thump = 0.3 * Math.exp(-ratio * 15);
            scale += thump;

            // Brightness flash on impact
            const flash = 4 * Math.exp(-ratio * 10);
            if (ratio > 0.8) {
                // Final dissipation flash
                ctx.filter = `brightness(${1 + (ratio - 0.8) * 10})`;
            } else {
                ctx.filter = `brightness(${1 + flash})`;
            }
        }

        ctx.globalAlpha = alpha;
        ctx.translate(currentX, currentY); // yOffset is built into currentY now
        ctx.scale(scale, scale);

        // Draw SVG (static body — animateTransform tags don't run on canvas)
        const size = CONSTANTS.CRYSTAL_SIZE;
        ctx.drawImage(crystalImg, -size / 2, -size / 2, size, size);

        // Draw the animated flares that the SVG can't animate on canvas
        drawAnimatedFlares(ctx, state, size);

        ctx.filter = 'none'; // reset filter
    }

    ctx.restore();
};

const drawShards = (ctx: CanvasRenderingContext2D, state: PurificationState) => {
    state.shards.forEach(shard => {
        const alpha = Math.max(0, 1 - (shard.life / shard.maxLife));
        ctx.save();
        ctx.translate(shard.x, shard.y);
        ctx.rotate(shard.rotation);

        ctx.shadowBlur = 15;
        ctx.shadowColor = shard.color;

        ctx.fillStyle = shard.color;
        ctx.globalAlpha = alpha;
        ctx.globalCompositeOperation = 'screen';

        // Draw diamond shard shape
        ctx.beginPath();
        ctx.moveTo(0, -shard.size);
        ctx.lineTo(shard.size / 2, 0);
        ctx.lineTo(0, shard.size);
        ctx.lineTo(-shard.size / 2, 0);
        ctx.closePath();
        ctx.fill();

        // White core highlight for shards
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(0, -shard.size * 0.4);
        ctx.lineTo(shard.size * 0.2, 0);
        ctx.lineTo(0, shard.size * 0.4);
        ctx.lineTo(-shard.size * 0.2, 0);
        ctx.closePath();
        ctx.fill();

        ctx.restore();
    });
};

const drawVolumetricBeam = (
    ctx: CanvasRenderingContext2D,
    x1: number, y1: number,
    x2: number, y2: number,
    globalAlpha: number
) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.hypot(dx, dy);
    if (length < 1) return;

    const angle = Math.atan2(dy, dx);

    ctx.save();
    ctx.translate(x1, y1);
    ctx.rotate(angle);
    ctx.globalAlpha = globalAlpha;
    ctx.globalCompositeOperation = 'screen';

    // To make it look "volumetric" and cinematic, we draw multiple overlapping polygons
    // with gradients, rather than standard lines. We also apply a visual "wobble" using sine.

    const time = Date.now() * 0.008; // Faster, violent time stepping

    // Helper to draw a dynamic, swirling energy strand
    const drawEnergyStrand = (
        width: number, color: string, opacity: number,
        freq: number, amp: number, phaseOffset: number
    ) => {
        ctx.beginPath();
        ctx.moveTo(0, 0);

        for (let d = 0; d <= length; d += 8) {
            // Taper at the ends so strands pinch together smoothly at source and tip
            const taper = Math.sin((d / length) * Math.PI);
            const yOffset = Math.sin(time * 3 + d * freq + phaseOffset) * amp * taper;
            ctx.lineTo(d, yOffset);
        }

        // Deep colored outer aura of the strand (no white core, per user request)
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.globalAlpha = opacity * globalAlpha;
        ctx.shadowBlur = width * 1.5;
        ctx.shadowColor = color;
        ctx.stroke();
    };

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // 1. Massive Background Haze Core
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(length, 0);
    ctx.strokeStyle = '#00aaff';
    ctx.lineWidth = 40;
    ctx.globalAlpha = 0.2 * globalAlpha;
    ctx.shadowBlur = 80;
    ctx.shadowColor = '#0055ff';
    ctx.stroke();

    // 2. Swirling Energy Strands (Double Helix-ish Chaos)
    drawEnergyStrand(10, '#00ffff', 0.8, 0.015, 40, 0);
    drawEnergyStrand(8, '#00aaff', 0.7, 0.025, 30, Math.PI);
    drawEnergyStrand(14, '#88ffff', 0.4, 0.01, 55, Math.PI / 2);
    drawEnergyStrand(12, '#0055ff', 0.5, 0.02, 45, Math.PI * 1.5);

    // 3. Piercing Blinding Inner Core (Straight) - Removed per user request to highlight the swirling waves

    ctx.shadowBlur = 0; // reset
    ctx.globalCompositeOperation = 'source-over';

    ctx.restore();
};

// Replicate all SVG <animateTransform> flare animations in canvas
// because ctx.drawImage() freezes SVG animations as a static raster.
const drawAnimatedFlares = (ctx: CanvasRenderingContext2D, state: PurificationState, crystalSize: number) => {
    // Use the effect's internal persistent time tracker rather than progress 
    // to guarantee it animates continuously across phase changes without jumping.
    const time = state.totalTime * 0.001 * 0.5; // seconds (scaled down slightly for better visual pacing)
    // The SVG viewBox is -150 -150 540 600, the crystal core is centered at (115, 130) in SVG coords.
    // On canvas, the crystal is drawn at (-size/2, -size/2, size, size).
    // The SVG viewBox width is 540, so the scale factor is crystalSize / 540.
    // The crystal core in SVG is at (115, 130). The viewBox starts at -150, -150.
    // So in the local image space: coreX = (115 - (-150)) / 540 * crystalSize - crystalSize/2
    //                              coreY = (130 - (-150)) / 600 * crystalSize - crystalSize/2
    const svgScale = crystalSize / 540;
    const coreLocalX = (115 + 150) * svgScale - crystalSize / 2;
    const coreLocalY = (130 + 150) * (crystalSize / 600) - crystalSize / 2;

    ctx.save();
    ctx.translate(coreLocalX, coreLocalY);
    ctx.globalCompositeOperation = 'screen';

    // Helper: draw a single thin radial ray (ellipse approximation via scaled arc)
    const drawRay = (rx: number, ry: number, opacity: number) => {
        ctx.save();
        ctx.scale(1, ry / rx || 0.01);
        ctx.beginPath();
        ctx.arc(0, 0, rx * svgScale, 0, Math.PI * 2);
        ctx.restore(); // restore scale before filling

        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, rx * svgScale);
        grad.addColorStop(0, `rgba(255,255,255,${opacity})`);
        grad.addColorStop(0.2, `rgba(136,255,255,${opacity * 0.7})`);
        grad.addColorStop(0.6, `rgba(0,136,255,${opacity * 0.2})`);
        grad.addColorStop(1, 'rgba(0,34,255,0)');
        ctx.fillStyle = grad;
        ctx.fill();
    };

    // 1. Primary Orthogonal Cross (Pulsing scale: 0.9 → 1.15 → 0.9, dur 2s)
    const pulse1 = 1 + 0.15 * Math.sin(time * Math.PI); // ~2s period
    ctx.save();
    ctx.scale(pulse1, pulse1);
    // Horizontal rays
    drawRay(120, 2, 0.7);
    drawRay(60, 6, 0.5);
    // Vertical rays  
    ctx.save();
    ctx.rotate(Math.PI / 2);
    drawRay(140, 2, 0.7);
    drawRay(70, 6, 0.5);
    ctx.restore();
    ctx.restore();

    // 2. Secondary Diagonal Starburst (Slow Rotation, dur 20s)
    const rot2 = (time / 20) * Math.PI * 2;
    ctx.save();
    ctx.rotate(rot2);
    const angles2 = [25, -25, 65, -65];
    const sizes2 = [100, 100, 80, 80];
    const opacities2 = [0.8, 0.8, 0.5, 0.5];
    for (let i = 0; i < angles2.length; i++) {
        ctx.save();
        ctx.rotate((angles2[i] * Math.PI) / 180);
        drawRay(sizes2[i], 1.5, opacities2[i]);
        ctx.restore();
    }
    ctx.restore();

    // 3. Tertiary Rapid Twinkle Rays (Fast counter-rotation dur 8s, pulsing scale dur 1.5s)
    const rot3 = -(time / 8) * Math.PI * 2; // counter-clockwise
    const pulse3 = 0.8 + 0.4 * Math.sin(time * (Math.PI * 2 / 1.5));
    ctx.save();
    ctx.rotate(rot3);
    ctx.scale(pulse3, pulse3);
    ctx.save();
    ctx.rotate((45 * Math.PI) / 180);
    drawRay(110, 0.8, 0.9);
    ctx.restore();
    ctx.save();
    ctx.rotate((-45 * Math.PI) / 180);
    drawRay(110, 0.8, 0.9);
    ctx.restore();
    ctx.restore();

    // 4. Giant Centered Counter-Rotating Rays (4x size)
    // Group A: Clockwise, dur 25s
    const rot4a = (time / 25) * Math.PI * 2;
    ctx.save();
    ctx.rotate(rot4a);
    ctx.save();
    ctx.rotate((15 * Math.PI) / 180);
    drawRay(400, 3, 0.85);
    ctx.restore();
    ctx.save();
    ctx.rotate((-75 * Math.PI) / 180);
    drawRay(400, 3, 0.85);
    ctx.restore();
    ctx.restore();

    // Group B: Counter-clockwise, dur 18s, pulsing 0.9-1.1 dur 3s
    const rot4b = -(time / 18) * Math.PI * 2;
    const pulse4b = 0.9 + 0.2 * Math.sin(time * (Math.PI * 2 / 3));
    ctx.save();
    ctx.rotate(rot4b);
    ctx.scale(pulse4b, pulse4b);
    ctx.save();
    ctx.rotate((45 * Math.PI) / 180);
    drawRay(360, 2, 0.9);
    ctx.restore();
    ctx.save();
    ctx.rotate((-45 * Math.PI) / 180);
    drawRay(360, 2, 0.9);
    ctx.restore();
    ctx.restore();

    // Hotspot glow at center
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 25 * svgScale);
    grad.addColorStop(0, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.15, 'rgba(224,255,255,0.7)');
    grad.addColorStop(0.4, 'rgba(0,204,255,0.4)');
    grad.addColorStop(1, 'rgba(0,34,255,0)');
    ctx.beginPath();
    ctx.arc(0, 0, 25 * svgScale, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.globalAlpha = 0.9;
    ctx.fill();

    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();
};
